import React from 'react';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { FontWeight, LARGE_TITLE_STYLES, Text } from '@clutter/clean';

import {
  GeocodeDocument,
  OrderCancellationPolicyGroupDocument,
} from '@graphql/platform';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';

import { Spacer } from '@shared/spacer';

import {
  CheckoutModal,
  ModalBodyParagraph,
  ModalCloseButton,
  ModalContainer,
  ModalCopySection,
  ModalCopyStack,
  ModalScrollableBody,
  ModalSectionHeading,
  ModalTitle,
} from '../steps/subcomponents/modal_shared';
import type { PolicyLearnMoreModalVariant } from './storage_cancellation_modal';
import { PolicyModal } from './subcomponents/policy_modal';

const Body = styled(Text.Body)`
  padding-top: 16px;
  padding-bottom: 40px;
`;

/** Billing / pre-auth copy (Due Today “When will I be charged?”). */
const SmartStorageBillingModalContent: React.FC<{
  isOpen: boolean;
  handleModalClose(): void;
  zip?: string | null;
  dateScheduled?: { fromTime: string } | null;
  variant?: PolicyLearnMoreModalVariant;
}> = ({
  isOpen,
  handleModalClose,
  zip,
  dateScheduled,
  variant = 'control',
}) => {
  const geocode =
    useQuery(GeocodeDocument, {
      variables: { zip: zip! },
      skip: !zip,
    }).data?.geocode ?? undefined;
  const { data } = useQuery(OrderCancellationPolicyGroupDocument, {
    variables: {
      visitorToken: WT_VISITOR_TOKEN,
      orderScheduledTime: dateScheduled?.fromTime,
    },
    skip: !geocode || geocode?.region?.name === 'Los Angeles',
  });

  const cancellationPolicy =
    data?.orderCancellationPolicyGroup?.onboardingCancellationPolicy;

  if (variant === 'challenger') {
    return (
      <CheckoutModal isOpen={isOpen} onClose={handleModalClose}>
        <ModalContainer>
          <ModalCloseButton onClose={handleModalClose} />
          <ModalScrollableBody>
            <ModalCopyStack>
              <ModalCopySection>
                <ModalTitle>When will I be charged?</ModalTitle>
                <ModalBodyParagraph>
                  Don’t worry, making a reservation is completely free! Your
                  first month of storage and pickup fee will be charged after
                  your appointment is completed.
                </ModalBodyParagraph>
                <ModalBodyParagraph>
                  We will perform a pre-authorization check with the card on
                  file 7 days before your appointment to verify sufficient funds
                  are available to cover the appointment. If you are booking
                  within 7 days of your appointment then we will perform the
                  check immediately after booking. The check will be immediately
                  released once the pre-authorization check is completed.
                </ModalBodyParagraph>
              </ModalCopySection>
              <ModalCopySection>
                <ModalSectionHeading>
                  What if I need to reschedule or cancel?
                </ModalSectionHeading>
                <ModalBodyParagraph>
                  {cancellationPolicy ? (
                    <>{cancellationPolicy.paragraph} </>
                  ) : (
                    <>
                      We ask that you reschedule or cancel at least 48hrs before
                      your appointment. Otherwise, you will be charged a $100
                      charge. This charge is increased to $200 on the same day
                      of the appointment.{' '}
                    </>
                  )}
                  You can reschedule or cancel online in just a few clicks.
                </ModalBodyParagraph>
              </ModalCopySection>
            </ModalCopyStack>
          </ModalScrollableBody>
        </ModalContainer>
      </CheckoutModal>
    );
  }

  return (
    <PolicyModal isOpen={isOpen} handleModalClose={() => handleModalClose()}>
      <Text style={LARGE_TITLE_STYLES.SM}>When will I be charged?</Text>
      <Body>
        Don’t worry, making a reservation is completely free! Your first month
        of storage and pickup fee will be charged after your appointment is
        completed.
        <Spacer height="12px" />
        We will perform a pre-authorization check with the card on file 7 days
        before your appointment to verify sufficient funds are available to
        cover the appointment. If you are booking within 7 days of your
        appointment then we will perform the check immediately after booking.
        The check will be immediately released once the pre-authorization check
        is completed.
      </Body>
      <Text.Headline weight={FontWeight.Medium}>
        What if I need to reschedule or cancel?
      </Text.Headline>
      <Body>
        {cancellationPolicy ? (
          cancellationPolicy.paragraph
        ) : (
          <>
            We ask that you reschedule or cancel at least 48hrs before your
            appointment. Otherwise, you will be charged a $100 charge. This
            charge is increased to $200 on the same day of the appointment.
          </>
        )}
        You can reschedule or cancel online in just a few clicks.
      </Body>
    </PolicyModal>
  );
};

export const SmartStorageBillingModal: React.FC<{
  isOpen: boolean;
  handleModalClose(): void;
}> = ({ isOpen, handleModalClose }) => {
  const {
    pricingSummary,
    flowState: { values },
  } = useStorageCheckoutContext();
  if (!pricingSummary) return null;

  return (
    <SmartStorageBillingModalContent
      isOpen={isOpen}
      handleModalClose={handleModalClose}
      zip={values.zip}
      dateScheduled={values.dateScheduled}
    />
  );
};
